import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { FiBarChart2, FiCheck } from "react-icons/fi";
import {
  BOARD_CONFIG,
  IS_MIX_CATALOG,
  MIX_REGISTERED_SLUGS,
} from "@/config/browserBoard";
import { AttachmentPicker } from "@/features/otegaki-upfile/components/AttachmentPicker";
import { useUpfileState } from "@/features/otegaki-upfile/hooks/useUpfile";
import { validateAttachmentSelection } from "@/features/otegaki-upfile/lib/attachmentSelection";
import {
  getAttachedFile,
  notifyUpfileSubmitted,
} from "@/features/otegaki-upfile/lib/attachUpfileImage";
import { PollEditor } from "@/features/poll/components/PollEditor";
import { usePollCapability } from "@/features/poll/lib/pollCapability";
import {
  POLL_CREATION_COOLDOWN_KEY,
  recordPollCreationCooldown,
  usePollCreationCooldownSeconds,
} from "@/features/poll/lib/pollCreationCooldown";
import {
  createEmptyPollEditorValue,
  normalizePollText,
  validatePollEditor,
} from "@/features/poll/lib/pollEditor";
import type { AltchaWidgetHandle } from "@/features/post/components/AltchaWidget";
import { ThreadFormAltchaWidget } from "@/features/post/components/FormAltchaWidget";
import { getCreatedThreadRoute } from "@/features/post/components/forms/createdThreadRoute";
import { DesktopFormField } from "@/features/post/components/forms/DesktopFormField";
import { DESKTOP_FORM_HELP_TEXT } from "@/features/post/components/forms/desktopFormHelpText";
import { MobileFormField } from "@/features/post/components/forms/MobileFormField";
import { OekakiSettingsDetails } from "@/features/post/components/forms/OekakiSettingsDetails";
import {
  formatPostBodyLength,
  POST_BODY_MAX_LENGTH,
} from "@/features/post/components/forms/postFormConfig";
import { deactivatePersistentForm } from "@/features/post/components/forms/postFormLifecycle";
import { createSubmissionLock } from "@/features/post/components/forms/submissionLock";
import {
  preventInputFormSubmitOnEnter,
  submitTextareaFormOnShortcut,
} from "@/features/post/components/forms/submitShortcut";
import { useSubmitPost } from "@/features/post/hooks/useSubmitPost";
import { createSubmissionLifecycle } from "@/features/post/lib/submissionLifecycle";
import {
  createSubmissionUiState,
  isSubmissionInProgress,
  submissionUiReducer,
} from "@/features/post/lib/submissionUiState";
import type {
  ModerationLevel,
  ThreadModerationInput,
} from "@/features/post/lib/submitPostFlow";
import { notifyDesktopSubmitSuccess } from "@/features/post/lib/submitPostSuccess";
import { useSettingsStore } from "@/features/settings/hooks";
import {
  resolveMixSelection,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { ThreadCreateBoardPicker } from "@/features/thread/components/forms/ThreadCreateBoardPicker";
import { useThreadLimits } from "@/features/thread/hooks/useThreadLimits";
import {
  clearThreadCreateDraft,
  readThreadCreateDraft,
} from "@/features/thread/stores/threadCreateDraftStore";
import {
  prepareThreadCreateAttachment,
  resolveThreadActInput,
} from "@/features/thread/utils/threadCreateSubmission";
import {
  collapseThreadDurationDays,
  EMPTY_THREAD_DURATION_PARTS,
  formatThreadDurationPayload,
  normalizeThreadDurationDays,
  resolveThreadDurationParts,
  THREAD_LIMITS_FALLBACK,
  type ThreadDurationInputMode,
  type ThreadDurationParts,
  type ThreadLimits,
  threadDurationDetails,
  threadDurationHint,
  validateThreadDurationParts,
} from "@/features/thread/utils/threadExpiry";
import { ApiError } from "@/shared/api";
import { cn } from "@/shared/lib";
import { getApiErrorMessage } from "@/shared/ui/feedback/apiErrorMessage";
import {
  Button,
  Checkbox,
  Input,
  PasswordInput,
  Textarea,
} from "@/shared/ui/form";
import { OnlineUsersIndicator, PostNotice } from "@/shared/ui/navigation";
import { toast } from "@/shared/ui/toast";

interface Props {
  onSuccess?: () => void;
  active?: boolean;
  className?: string;
  /**
   * PC版の旧UI風フローティングパネル内で描画される場合にtrue。
   * 保持期間フィールドを .desktop-floating-field グリッド
   * （ラベル72px+入力1fr）で描画する。SP版（ThreadCreateModal経由）は
   * 指定しないため、見た目・DOM構造に影響しない。
   */
  showDesktopLabels?: boolean;
}

const SCOPE_NAME = "thread-create-form";
const UPFILE_FULL_KEY = `${SCOPE_NAME}:upfile`;
// 既定はR18画像禁止 (=非R18スレ)。「R18画像を許可」チェックで nsfw:"off" になり
// サーバーがR18タグを付与する。性的な文章は既定で許可のまま (R18導出には関与しない)。
const DEFAULT_THREAD_MODERATION: ThreadModerationInput = {
  text: { sexual: "off", exclusion: "standard", abuse: "lenient" },
  image: { nsfw: "on" },
};

function forceNonR18Moderation(
  moderation: ThreadModerationInput,
): ThreadModerationInput {
  if (moderation.image.nsfw === "on") return moderation;
  return { ...moderation, image: { nsfw: "on" } };
}

export const ThreadCreateForm: React.FunctionComponent<Props> = ({
  onSuccess,
  active = true,
  className,
  showDesktopLabels = false,
}: Props) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const deleteKey = useSettingsStore((state) => state.deleteKey);
  const boardSlug = BOARD_CONFIG.boardKey;
  // 総合カタログではONの板(制御なし+%)から立て先を選べる
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const mixTargetSlugs = useMemo((): readonly string[] => {
    if (!IS_MIX_CATALOG) return [];
    const selection = resolveMixSelection(
      MIX_REGISTERED_SLUGS,
      mixBoardSettings,
    );
    if (selection === null) return [];
    const enabled = new Set([
      ...selection.uncappedSlugs,
      ...selection.extras.map((extra) => extra.slug),
    ]);
    return MIX_REGISTERED_SLUGS.filter((slug) => enabled.has(slug));
  }, [mixBoardSettings]);
  const [targetBoardSlug, setTargetBoardSlug] = useState(boardSlug);
  // 設定変更で選択板がOFFになった場合はONの板の先頭へ縮退する。
  // アンカー板がOFFのときに立て先へ復活させないため、boardSlugへは戻さない。
  const effectiveTargetSlug =
    IS_MIX_CATALOG && mixTargetSlugs.length > 0
      ? mixTargetSlugs.includes(targetBoardSlug)
        ? targetBoardSlug
        : (mixTargetSlugs[0] ?? boardSlug)
      : boardSlug;
  const [body, setBody] = useState(() => readThreadCreateDraft(boardSlug));
  const updateBody = useCallback((nextBody: string): void => {
    setBody(nextBody);
  }, []);
  const [moderation, setModeration] = useState<ThreadModerationInput>(
    DEFAULT_THREAD_MODERATION,
  );
  const [allowImageReplies, setAllowImageReplies] = useState(true);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const pollCapability = usePollCapability();
  const [pollEditor, setPollEditor] = useState(createEmptyPollEditorValue);
  const [showPollErrors, setShowPollErrors] = useState(false);
  const pollValidation = validatePollEditor(pollEditor);
  const [actInput, setActInput] = useState("");
  const [durationInputParts, setDurationInputParts] =
    useState<ThreadDurationParts>(EMPTY_THREAD_DURATION_PARTS);
  const [wasDurationEdited, setWasDurationEdited] = useState(false);
  const previousDurationInputModeRef = useRef<ThreadDurationInputMode | null>(
    null,
  );
  const [submissionUi, dispatchSubmissionUi] = useReducer(
    submissionUiReducer,
    undefined,
    createSubmissionUiState,
  );
  const updateAttachmentFiles = useCallback((files: File[]): void => {
    dispatchSubmissionUi({ type: "edit" });
    setAttachmentFiles(files);
  }, []);
  const updateModeration = useCallback(
    (update: React.SetStateAction<ThreadModerationInput>): void => {
      dispatchSubmissionUi({ type: "edit" });
      setModeration(update);
    },
    [],
  );
  const updateTargetBoardSlug = useCallback(
    (slug: string): void => {
      if (slug !== effectiveTargetSlug) {
        dispatchSubmissionUi({ type: "edit" });
      }
      setTargetBoardSlug(slug);
    },
    [effectiveTargetSlug],
  );
  const [submissionLock] = useState(createSubmissionLock);
  const [submissionLifecycle] = useState(createSubmissionLifecycle);
  const altchaRef = useRef<AltchaWidgetHandle>(null);
  const upfileState = useUpfileState(UPFILE_FULL_KEY);
  const isPaintPopupOpen = Boolean(
    upfileState?.isAxnosOpen || upfileState?.isKlecksOpen,
  );
  const hasPendingUpfile = Boolean(
    upfileState?.hasSelectedFile || upfileState?.isHacchanOpen,
  );
  const { mutateAsync: submitPost, isPending } = useSubmitPost();
  const { data: threadLimits, isError: isThreadLimitsError } =
    useThreadLimits(effectiveTargetSlug);
  const hasAuthoritativeThreadLimits =
    threadLimits !== undefined && !isThreadLimitsError;
  const allowR18ThreadCreation = threadLimits?.allowR18ThreadCreation !== false;
  const previousAllowR18ThreadCreationRef = useRef(allowR18ThreadCreation);
  const submitErrorIncludesR18ThreadSettingHintRef = useRef(false);
  const isThreadLimitsUnavailable = !hasAuthoritativeThreadLimits;
  const isUsingThreadLimitsFallback =
    threadLimits === undefined && isThreadLimitsError;
  const effectiveThreadLimits = isUsingThreadLimitsFallback
    ? THREAD_LIMITS_FALLBACK
    : threadLimits;
  const resolvedDurationParts = isThreadLimitsUnavailable
    ? EMPTY_THREAD_DURATION_PARTS
    : resolveThreadDurationParts(
        durationInputParts,
        effectiveThreadLimits,
        wasDurationEdited,
      );
  const durationInputMode: ThreadDurationInputMode =
    effectiveThreadLimits?.inputMode ?? "hours_minutes";
  const supportsDurationDays = durationInputMode === "days_hours_minutes";
  const durationParts = supportsDurationDays
    ? resolvedDurationParts
    : collapseThreadDurationDays(resolvedDurationParts);
  const updateDurationPart = useCallback(
    (part: keyof ThreadDurationParts, value: string): void => {
      setDurationInputParts({ ...durationParts, [part]: value });
      setWasDurationEdited(true);
    },
    [durationParts],
  );
  const durationError =
    effectiveThreadLimits && !isThreadLimitsUnavailable
      ? validateThreadDurationParts(
          durationParts,
          effectiveThreadLimits.durationHours,
          effectiveThreadLimits.minimumMinutes,
          durationInputMode,
        )
      : null;
  const durationHelpText = effectiveThreadLimits
    ? `${DESKTOP_FORM_HELP_TEXT.threadDuration}\n${threadDurationDetails(
        effectiveThreadLimits,
        isThreadLimitsError,
      )}`
    : DESKTOP_FORM_HELP_TEXT.threadDuration;
  const isSubmitting = isSubmissionInProgress(submissionUi, isPending);
  const { showSuccess, submitError } = submissionUi;
  const hasRequiredContent =
    attachmentFiles.length === 1 ||
    (attachmentFiles.length === 0 && hasPendingUpfile);
  const pollCooldownSeconds = usePollCreationCooldownSeconds(
    POLL_CREATION_COOLDOWN_KEY,
  );

  useEffect(() => {
    return (): void => submissionLifecycle.invalidate();
  }, [submissionLifecycle]);

  useEffect(() => {
    if (isThreadLimitsUnavailable) return;
    const previousDurationInputMode = previousDurationInputModeRef.current;
    previousDurationInputModeRef.current = durationInputMode;
    if (
      previousDurationInputMode === null ||
      previousDurationInputMode === durationInputMode
    ) {
      return;
    }
    setDurationInputParts((previous) =>
      durationInputMode === "days_hours_minutes"
        ? normalizeThreadDurationDays(previous)
        : collapseThreadDurationDays(previous),
    );
  }, [durationInputMode, isThreadLimitsUnavailable]);

  useEffect(() => {
    if (!active) {
      deactivatePersistentForm({
        invalidate: submissionLifecycle.invalidate,
        release: submissionLock.release,
        resetAltcha: (): void => altchaRef.current?.reset(),
        // Closing a persistent form must make the next open immediately usable.
        resetUi: (): void => dispatchSubmissionUi({ type: "reset" }),
      });
      setPollEditor(createEmptyPollEditorValue());
      setShowPollErrors(false);
    }
  }, [active, submissionLifecycle, submissionLock]);

  useEffect(() => {
    if (pollCapability === "available") return;
    setPollEditor(createEmptyPollEditorValue());
    setShowPollErrors(false);
  }, [pollCapability]);

  useEffect(() => {
    if (allowR18ThreadCreation) return;
    setModeration(forceNonR18Moderation);
  }, [allowR18ThreadCreation]);

  useEffect(() => {
    const previouslyAllowed = previousAllowR18ThreadCreationRef.current;
    previousAllowR18ThreadCreationRef.current = allowR18ThreadCreation;
    if (
      previouslyAllowed &&
      !allowR18ThreadCreation &&
      submitError &&
      submitErrorIncludesR18ThreadSettingHintRef.current
    ) {
      // 制限変更で不可能になった案内だけを消し、他の投稿エラーは保持する。
      submitErrorIncludesR18ThreadSettingHintRef.current = false;
      dispatchSubmissionUi({ type: "edit" });
    }
  }, [allowR18ThreadCreation, submitError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: target board is an external form identity boundary
  useEffect(() => {
    dispatchSubmissionUi({ type: "edit" });
    setPollEditor(createEmptyPollEditorValue());
    setShowPollErrors(false);
  }, [effectiveTargetSlug]);

  const handleSubmit = async (
    event: React.SubmitEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const isPollEnabled =
      pollCapability === "available" && pollEditor.isEnabled;
    if (
      (isPollEnabled && normalizePollText(body).length === 0) ||
      (isPollEnabled && pollValidation.payload === null) ||
      (isPollEnabled && pollCooldownSeconds > 0)
    ) {
      setShowPollErrors(true);
      return;
    }
    if (
      !active ||
      submissionLock.isLocked() ||
      isPaintPopupOpen ||
      !hasRequiredContent ||
      body.length > POST_BODY_MAX_LENGTH ||
      durationError !== null
    ) {
      return;
    }
    if (!submissionLock.acquire()) return;
    const signal = submissionLifecycle.begin();
    dispatchSubmissionUi({ type: "start" });

    const form = event.currentTarget;
    let files = attachmentFiles;
    let submissionAccepted = false;
    const submittedModeration = allowR18ThreadCreation
      ? moderation
      : forceNonR18Moderation(moderation);
    try {
      if (files.length === 0) {
        files = [await prepareThreadCreateAttachment(form, getAttachedFile)];
      }
      const selectionValidation = validateAttachmentSelection("thread", files);
      if (!selectionValidation.valid) {
        throw new Error(selectionValidation.error ?? "");
      }
      if (!submissionLifecycle.isCurrent(signal)) return;
    } catch (error) {
      if (!submissionLifecycle.isCurrent(signal)) return;
      submitErrorIncludesR18ThreadSettingHintRef.current = false;
      toast.error(
        error instanceof Error ? error.message : "画像の準備に失敗しました",
      );
      dispatchSubmissionUi({
        type: "failure",
        message: getApiErrorMessage(error, "画像の準備に失敗しました"),
      });
      submissionLock.release();
      dispatchSubmissionUi({ type: "finish" });
      return;
    }

    try {
      const altcha = altchaRef.current;
      if (!altcha) throw new Error("書き込み認証を読み込んでいます");
      const threadAct = resolveThreadActInput(actInput);
      const result = await submitPost({
        mode: "thread",
        body: body.trim(),
        deleteKey,
        files,
        moderation: submittedModeration,
        allowImageReplies,
        duration: isThreadLimitsUnavailable
          ? ""
          : formatThreadDurationPayload(durationParts),
        sage: threadAct.sage,
        showId: threadAct.showId,
        ...(isPollEnabled && pollValidation.payload
          ? { poll: pollValidation.payload }
          : {}),
        ...(IS_MIX_CATALOG ? { boardKey: effectiveTargetSlug } : {}),
        altcha,
        signal,
      });
      if (!submissionLifecycle.isCurrent(signal)) return;
      submissionAccepted = true;
      // PC版は作成直後に遷移するため、画面に残る通知で完了を伝える。
      notifyDesktopSubmitSuccess("thread", showDesktopLabels, (message) =>
        toast.success(message),
      );
      notifyUpfileSubmitted(form);
      setAttachmentFiles([]);
      setPollEditor(createEmptyPollEditorValue());
      setShowPollErrors(false);
      dispatchSubmissionUi({ type: "success" });
      clearThreadCreateDraft(boardSlug);
      setBody("");
      setModeration(DEFAULT_THREAD_MODERATION);
      setAllowImageReplies(true);
      setActInput("");
      setDurationInputParts(EMPTY_THREAD_DURATION_PARTS);
      setWasDurationEdited(false);
      onSuccess?.();
      if (IS_MIX_CATALOG) {
        // 立てた板のスレページへmixマウント内でSPA遷移する(miniplayer維持)
        void router.navigate(
          getCreatedThreadRoute(result.threadId, effectiveTargetSlug),
        );
        return;
      }
      void router.navigate(getCreatedThreadRoute(result.threadId));
    } catch (error) {
      if (!submissionLifecycle.isCurrent(signal)) return;
      // API成功後のローカル初期化失敗を、作成失敗として利用者へ表示しない。
      if (submissionAccepted) return;
      if (
        error instanceof ApiError &&
        error.code === "POLL_CREATION_RATE_LIMITED"
      ) {
        recordPollCreationCooldown(
          POLL_CREATION_COOLDOWN_KEY,
          error.retryAfter,
        );
      }
      // 再描画より先に応答が届いても、送信先の最新の制限から案内を決める。
      const latestThreadLimits = queryClient.getQueryState<ThreadLimits>([
        "thread-limits",
        effectiveTargetSlug,
      ]);
      const shouldIncludeR18ThreadSettingHint =
        submittedModeration.image.nsfw !== "off" &&
        latestThreadLimits?.status === "success" &&
        latestThreadLimits.data?.allowR18ThreadCreation !== false;
      submitErrorIncludesR18ThreadSettingHintRef.current =
        error instanceof ApiError &&
        error.code === "NSFW_REJECTED" &&
        shouldIncludeR18ThreadSettingHint;
      // 共通toastを抑止するエラーも、フォーム内では文脈付きで表示する。
      dispatchSubmissionUi({
        type: "failure",
        message:
          error instanceof ApiError &&
          error.code === "POLL_CREATION_RATE_LIMITED"
            ? "アンケート付き投稿は少し時間を空けてください。"
            : getApiErrorMessage(error, "スレッド作成に失敗しました", {
                includeR18ThreadSettingHint: shouldIncludeR18ThreadSettingHint,
              }),
      });
    } finally {
      if (submissionLifecycle.isCurrent(signal)) {
        submissionLock.release();
        dispatchSubmissionUi({ type: "finish" });
      }
    }
  };

  const durationLabelId = "thread-duration-label";
  const bodyLabelId = "thread-body-label";
  const attachmentLabelId = "thread-attachment-label";
  const actLabelId = "thread-act-label";
  const boardLabelId = "thread-board-label";
  const deleteKeyLabelId = "thread-delete-key-label";
  const optionsLabelId = "thread-options-label";
  const authenticationLabelId = "thread-authentication-label";
  const durationDescribedBy = `thread-duration-hint${durationError ? " thread-duration-error" : ""}`;
  const pollQuestionErrorId = "thread-poll-question-error";
  const hasPollQuestionError =
    pollCapability === "available" &&
    pollEditor.isEnabled &&
    normalizePollText(body).length === 0;

  const durationInputs = (
    <fieldset
      className="flex min-w-0 items-center gap-2"
      aria-labelledby={durationLabelId}
    >
      <legend className="sr-only">保持期間</legend>
      {supportsDurationDays && (
        <>
          <Input
            id="thread-duration-days"
            type="number"
            min={0}
            max={
              effectiveThreadLimits
                ? Math.floor(effectiveThreadLimits.durationHours / 24)
                : undefined
            }
            step={1}
            value={durationParts.days}
            onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
              updateDurationPart("days", event.target.value)
            }
            onKeyDown={preventInputFormSubmitOnEnter}
            inputMode="numeric"
            autoComplete="off"
            disabled={isThreadLimitsUnavailable}
            className="w-16"
            aria-describedby={durationDescribedBy}
            aria-invalid={durationError !== null}
            error={durationError !== null}
          />
          <label
            htmlFor="thread-duration-days"
            className="text-sm text-foreground"
          >
            日
          </label>
        </>
      )}
      <Input
        id="thread-duration-hours"
        type="number"
        min={0}
        max={supportsDurationDays ? 23 : undefined}
        step={1}
        value={durationParts.hours}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          updateDurationPart("hours", event.target.value)
        }
        onKeyDown={preventInputFormSubmitOnEnter}
        inputMode="numeric"
        autoComplete="off"
        disabled={isThreadLimitsUnavailable}
        className={supportsDurationDays ? "w-16" : "w-20"}
        aria-describedby={durationDescribedBy}
        aria-invalid={durationError !== null}
        error={durationError !== null}
      />
      <label
        htmlFor="thread-duration-hours"
        className="text-sm text-foreground"
      >
        時間
      </label>
      <Input
        id="thread-duration-minutes"
        type="number"
        min={0}
        max={59}
        step={1}
        value={durationParts.minutes}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          updateDurationPart("minutes", event.target.value)
        }
        onKeyDown={preventInputFormSubmitOnEnter}
        inputMode="numeric"
        autoComplete="off"
        disabled={isThreadLimitsUnavailable}
        className={supportsDurationDays ? "w-16" : "w-20"}
        aria-describedby={durationDescribedBy}
        aria-invalid={durationError !== null}
        error={durationError !== null}
      />
      <label
        htmlFor="thread-duration-minutes"
        className="text-sm text-foreground"
      >
        分
      </label>
    </fieldset>
  );

  const durationMeta = (
    <>
      <p id="thread-duration-hint" className="text-xs text-muted-foreground">
        {effectiveThreadLimits
          ? threadDurationHint(effectiveThreadLimits)
          : "現在の保持期間上限を取得中です…"}
      </p>
      {durationError && (
        <p
          id="thread-duration-error"
          className="text-xs text-destructive"
          role="alert"
        >
          {durationError}
        </p>
      )}
    </>
  );

  // showDesktopLabels時はPC版の左ラベル付きグリッド行として描画する。
  // SP版（false）は小見出しとヘルプ付きのブロックとして描画する。
  const durationField = showDesktopLabels ? (
    // PC版のラベル列は狭いため、SP版より短い文言を使う。
    <DesktopFormField
      label="スレ寿命"
      labelId={durationLabelId}
      fieldClassName="desktop-thread-field-duration"
      contentClassName="space-y-1"
      helpText={durationHelpText}
    >
      {durationInputs}
      {durationMeta}
    </DesktopFormField>
  ) : (
    <MobileFormField
      label="スレッド寿命"
      labelId={durationLabelId}
      helpText={durationHelpText}
    >
      {durationInputs}
      {durationMeta}
    </MobileFormField>
  );

  const successMessage = showSuccess && (
    <output className="desktop-thread-create-success flex items-center gap-2 rounded-lg bg-green-600 px-4 py-3 text-white">
      <FiCheck aria-hidden="true" className="h-4 w-4" />
      <strong className="font-medium">成功しました！</strong>
    </output>
  );

  const bodyInput = (
    <Textarea
      value={body}
      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        dispatchSubmissionUi({ type: "edit" });
        updateBody(event.target.value);
      }}
      onKeyDown={submitTextareaFormOnShortcut}
      placeholder={showDesktopLabels ? "本文を入力" : "ｷﾀ━━━━(ﾟ∀ﾟ)━━━━!!"}
      rows={6}
      maxLength={POST_BODY_MAX_LENGTH}
      aria-labelledby={bodyLabelId}
      aria-describedby={`thread-body-counter${showPollErrors && hasPollQuestionError ? ` ${pollQuestionErrorId}` : ""}`}
      aria-invalid={showPollErrors && hasPollQuestionError}
      error={showPollErrors && hasPollQuestionError}
    />
  );

  const bodyCounter = (
    <p
      id="thread-body-counter"
      className="text-right text-xs text-muted-foreground"
    >
      {formatPostBodyLength(body.length)}
    </p>
  );

  const bodyField = (
    <>
      {bodyInput}
      {bodyCounter}
      {showPollErrors && hasPollQuestionError && (
        <p
          id={pollQuestionErrorId}
          role="alert"
          className="text-xs text-destructive"
        >
          アンケート付き投稿では、コメントを質問として入力してください。
        </p>
      )}
    </>
  );

  const pollEditorPanelId = "thread-poll-editor";
  const pollEditorContent =
    pollCapability === "available" && pollEditor.isEnabled ? (
      <div id={pollEditorPanelId} className="space-y-1">
        <PollEditor
          value={pollEditor}
          validation={pollValidation}
          onChange={setPollEditor}
          isDisabled={isSubmitting}
          showAllErrors={showPollErrors}
        />
        {pollCooldownSeconds > 0 && (
          <p role="status" className="text-xs text-destructive">
            アンケート付き投稿はあと{pollCooldownSeconds}秒待ってください。
            アンケートを取り消せば通常投稿できます。
          </p>
        )}
      </div>
    ) : null;

  const attachmentField = (
    <fieldset className="space-y-3">
      <legend className="sr-only">添付</legend>
      <AttachmentPicker
        scopeName={SCOPE_NAME}
        fullKey={UPFILE_FULL_KEY}
        mode="thread"
        files={attachmentFiles}
        onChange={updateAttachmentFiles}
        allowImage
        disabled={isSubmitting}
        surface={showDesktopLabels ? "desktop" : "mobile"}
        trailingAction={
          pollCapability === "available"
            ? {
                label: "アンケート",
                icon: <FiBarChart2 aria-hidden="true" className="h-5 w-11" />,
                onClick: (): void => {
                  setShowPollErrors(false);
                  setPollEditor({
                    ...pollEditor,
                    isEnabled: !pollEditor.isEnabled,
                  });
                },
                isDisabled: isSubmitting,
                isExpanded: pollEditor.isEnabled,
                controls: pollEditor.isEnabled ? pollEditorPanelId : undefined,
              }
            : undefined
        }
        actionExpandedContent={pollEditorContent}
      />
      <OekakiSettingsDetails />
    </fieldset>
  );

  const actInputField = (
    <Input
      id="thread-create-act"
      type="text"
      value={actInput}
      onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
        setActInput(event.target.value)
      }
      onKeyDown={preventInputFormSubmitOnEnter}
      placeholder="sage, ID表示"
      autoComplete="off"
      className={showDesktopLabels ? undefined : "w-full"}
      aria-labelledby={actLabelId}
    />
  );

  const actField = showDesktopLabels ? (
    <DesktopFormField
      label="ACT"
      labelId={actLabelId}
      fieldClassName="desktop-thread-create-field-act"
    >
      {actInputField}
    </DesktopFormField>
  ) : (
    <MobileFormField label="ACT" labelId={actLabelId}>
      {actInputField}
    </MobileFormField>
  );

  const optionControls = (
    <fieldset
      className={
        showDesktopLabels
          ? "desktop-thread-create-options space-y-3"
          : "space-y-3"
      }
    >
      <legend className="sr-only">投稿オプション</legend>
      <fieldset className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <legend className="sr-only">画像オプション</legend>
        <Checkbox
          checked={moderation.image.nsfw === "off"}
          onChange={(allowR18Images: boolean): void =>
            updateModeration((current) => ({
              ...current,
              image: { nsfw: allowR18Images ? "off" : "on" },
            }))
          }
          label="R18画像を許可"
          disabled={!allowR18ThreadCreation}
        />
        <Checkbox
          checked={allowImageReplies}
          onChange={setAllowImageReplies}
          label="画像レスを許可"
        />
      </fieldset>
      <details>
        <summary className="cursor-pointer text-sm font-medium text-foreground">
          検閲設定
        </summary>
        <fieldset className="mt-2 grid gap-2 sm:grid-cols-2">
          <legend className="sr-only">検閲項目</legend>
          <ModerationSelect
            label="性的な文章"
            value={moderation.text.sexual}
            options={[
              ["on", "禁止"],
              ["off", "許可"],
            ]}
            onChange={(sexual: "off" | "on"): void =>
              updateModeration((current) => ({
                ...current,
                text: { ...current.text, sexual },
              }))
            }
          />
          <ModerationLevelSelect
            label="排他・自治"
            value={moderation.text.exclusion}
            onChange={(exclusion: ModerationLevel): void =>
              updateModeration((current) => ({
                ...current,
                text: { ...current.text, exclusion },
              }))
            }
          />
          <ModerationLevelSelect
            label="誹謗中傷・脅迫"
            value={moderation.text.abuse}
            onChange={(abuse: ModerationLevel): void =>
              updateModeration((current) => ({
                ...current,
                text: { ...current.text, abuse },
              }))
            }
          />
        </fieldset>
      </details>
    </fieldset>
  );

  const optionsField = optionControls;

  const authenticationField = (
    <ThreadFormAltchaWidget active={active} widgetRef={altchaRef} />
  );

  const onlineUsers = (
    <OnlineUsersIndicator className="desktop-thread-create-online block text-center text-xs text-muted-foreground" />
  );

  const submitButton = (
    <Button
      type="submit"
      variant="accent-action"
      disabled={
        isSubmitting ||
        isPaintPopupOpen ||
        !hasRequiredContent ||
        body.length > POST_BODY_MAX_LENGTH ||
        durationError !== null
      }
      className="desktop-thread-create-submit w-full py-4 text-lg font-medium"
    >
      {isSubmitting
        ? "書き込み中..."
        : showDesktopLabels
          ? "新規スレッド (Ctrl+Enter)"
          : "スレッドを立てる"}
    </Button>
  );

  const submitErrorMessage = submitError && (
    <p
      className={
        showDesktopLabels
          ? "desktop-thread-create-error text-sm"
          : "text-sm text-destructive"
      }
      role="alert"
    >
      {submitError}
    </p>
  );

  const boardPickerField =
    IS_MIX_CATALOG && mixTargetSlugs.length > 1 ? (
      showDesktopLabels ? (
        <DesktopFormField
          label="板"
          labelId={boardLabelId}
          fieldClassName="desktop-thread-create-field-board"
          groupContent
        >
          <ThreadCreateBoardPicker
            slugs={mixTargetSlugs}
            selectedSlug={effectiveTargetSlug}
            onSelect={updateTargetBoardSlug}
          />
        </DesktopFormField>
      ) : (
        <MobileFormField label="スレ立て先" labelId={boardLabelId} groupContent>
          <ThreadCreateBoardPicker
            slugs={mixTargetSlugs}
            selectedSlug={effectiveTargetSlug}
            onSelect={updateTargetBoardSlug}
          />
        </MobileFormField>
      )
    ) : null;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        showDesktopLabels ? "desktop-thread-create-form" : "space-y-4",
        className,
      )}
    >
      {showDesktopLabels ? (
        <>
          {submitErrorMessage}
          {successMessage}
          {boardPickerField}
          {actField}
          <DesktopFormField
            label="コメント"
            labelId={bodyLabelId}
            fieldClassName="desktop-thread-create-field-comment"
            contentClassName="desktop-thread-create-body"
          >
            {bodyField}
          </DesktopFormField>
          <DesktopFormField
            label="添付"
            labelId={attachmentLabelId}
            fieldClassName="desktop-thread-create-field-attachment"
            groupContent
            helpText={DESKTOP_FORM_HELP_TEXT.attachment}
          >
            {attachmentField}
          </DesktopFormField>
          <DesktopFormField
            label="削除キー"
            labelId={deleteKeyLabelId}
            fieldClassName="desktop-thread-create-field-delete-key"
          >
            <PasswordInput
              value={deleteKey}
              readOnly
              aria-labelledby={deleteKeyLabelId}
              visibilityLabel="削除キー"
            />
          </DesktopFormField>
          {durationField}
          <DesktopFormField
            label="詳細設定"
            labelId={optionsLabelId}
            fieldClassName="desktop-thread-create-field-options"
            groupContent
            helpText={DESKTOP_FORM_HELP_TEXT.options}
          >
            {optionsField}
          </DesktopFormField>
          <DesktopFormField
            label="認証"
            labelId={authenticationLabelId}
            fieldClassName="desktop-thread-create-field-authentication"
            groupContent
            helpText={DESKTOP_FORM_HELP_TEXT.authentication}
          >
            {authenticationField}
          </DesktopFormField>
          {submitButton}
          {onlineUsers}
          <PostNotice />
        </>
      ) : (
        <>
          {successMessage}
          {boardPickerField}
          {actField}
          <MobileFormField label="コメント" labelId={bodyLabelId}>
            {bodyField}
          </MobileFormField>
          <MobileFormField
            label="添付ファイル"
            labelId={attachmentLabelId}
            groupContent
            helpText={DESKTOP_FORM_HELP_TEXT.attachment}
          >
            {attachmentField}
          </MobileFormField>

          <MobileFormField
            label="オプション"
            labelId={optionsLabelId}
            groupContent
            helpText={DESKTOP_FORM_HELP_TEXT.options}
          >
            {optionsField}
          </MobileFormField>
          {durationField}
          <MobileFormField
            label="認証"
            labelId={authenticationLabelId}
            groupContent
            helpText={DESKTOP_FORM_HELP_TEXT.authentication}
          >
            {authenticationField}
          </MobileFormField>
          {submitButton}
          {onlineUsers}
          <PostNotice />
          {submitErrorMessage}
        </>
      )}
    </form>
  );
};

const MODERATION_LEVEL_OPTIONS: ReadonlyArray<
  readonly [ModerationLevel, string]
> = [
  ["off", "無効"],
  ["lenient", "弱"],
  ["standard", "標準"],
  ["strict", "強"],
];

function ModerationLevelSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: ModerationLevel;
  onChange: (value: ModerationLevel) => void;
}): React.ReactNode {
  return (
    <ModerationSelect
      label={label}
      value={value}
      options={MODERATION_LEVEL_OPTIONS}
      onChange={onChange}
    />
  );
}

function ModerationSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<readonly [T, string]>;
  onChange: (value: T) => void;
}): React.ReactNode {
  return (
    <label className="flex items-center justify-between gap-2 text-xs text-foreground">
      {label}
      <select
        aria-label={label}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLSelectElement>): void =>
          onChange(event.target.value as T)
        }
        className="rounded-md border border-border bg-card px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}
